<template>
  <div
    v-for="def in CHANNELS"
    :key="def.label"
    class="flex min-w-0 grow flex-col items-center gap-1"
  >
    <input
      v-model="draft[def.id]"
      class="w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 text-center text-sm focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-500"
      inputmode="numeric"
      pattern="[0-9]*"
      @blur="reset()"
    />
    <span class="sr-only">{{ def.label }}</span>
  </div>
</template>

<script setup lang="ts">
import {
  type Hsl,
  type Rgb,
  convertHslToRgb,
  convertRgbToHsl,
} from "culori/fn";
import isDeepEqual from "fast-deep-equal";
import { ref, watch } from "vue";

const CHANNELS = [
  { id: "r", label: "R" },
  { id: "g", label: "G" },
  { id: "b", label: "B" },
] as const;

const modelValue = defineModel<Hsl>({ required: true });

let snapshot: Rgb | undefined;
const draft = ref({ r: "0", g: "0", b: "0" });

const reset = () => {
  if (!snapshot) {
    return;
  }

  draft.value = {
    r: "" + Math.round(snapshot.r * 255),
    g: "" + Math.round(snapshot.g * 255),
    b: "" + Math.round(snapshot.b * 255),
  };
};

function quantize(rgb: Rgb): Rgb {
  return {
    mode: "rgb",
    r: Math.round(rgb.r * 255) / 255,
    g: Math.round(rgb.g * 255) / 255,
    b: Math.round(rgb.b * 255) / 255,
  };
}

watch(
  modelValue,
  (hsl) => {
    const rgb = quantize(convertHslToRgb(hsl));
    if (isDeepEqual(rgb, snapshot)) {
      return;
    }

    snapshot = rgb;
    reset();
  },
  { immediate: true }
);

const parseChannel = (raw: string, min: number, max: number): number => {
  let num = Number(raw);
  if (!Number.isFinite(num)) {
    num = 0;
  }

  return Math.max(min, Math.min(num, max));
};

watch(
  draft,
  (draft) => {
    const r = parseChannel(draft.r, 0, 255) / 255;
    const g = parseChannel(draft.g, 0, 255) / 255;
    const b = parseChannel(draft.b, 0, 255) / 255;

    const next: Rgb = { mode: "rgb", r, g, b };
    if (isDeepEqual(next, snapshot)) {
      return;
    }

    snapshot = next;
    modelValue.value = convertRgbToHsl(next);
  },
  { deep: true }
);
</script>
